import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import SuccessPopup from '../components/SuccessPopup';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

function Cart({ cart, onUpdateQuantity, onRemove, onRemoveMultiple, user }) {
  const navigate = useNavigate();
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    province_id: '',
    province_name: '',
    city_id: '',
    city_name: '',
    district_id: '',
    district_name: ''
  });
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    const loadProvinces = async () => {
      const result = await api.getProvinces();
      if (result.success) setProvinces(result.data);
    };
    loadProvinces();
  }, []);

  useEffect(() => {
    if (customerInfo.province_id) {
      const loadCities = async () => {
        const result = await api.getCities(customerInfo.province_id);
        if (result.success) setCities(result.data);
        setCustomerInfo(prev => ({...prev, city_id: '', city_name: '', district_id: '', district_name: ''}));
        setDistricts([]);
      };
      loadCities();
    }
  }, [customerInfo.province_id]);

  useEffect(() => {
    if (customerInfo.city_id) {
      const loadDistricts = async () => {
        const result = await api.getDistricts(customerInfo.city_id);
        if (result.success) setDistricts(result.data);
        setCustomerInfo(prev => ({...prev, district_id: '', district_name: ''}));
      };
      loadDistricts();
    }
  }, [customerInfo.city_id]);

  const toggleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === cart.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(cart.map(item => item.id));
    }
  };

  const selectedCartItems = cart.filter(item => selectedItems.includes(item.id));
  const total = selectedCartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleCheckout = () => {
    if (selectedItems.length === 0) {
      alert('Please select items to checkout');
      return;
    }
    setShowCheckoutModal(true);
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    const orderData = {
      customer_name: customerInfo.name,
      customer_phone: customerInfo.phone,
      customer_email: customerInfo.email,
      customer_address: customerInfo.address,
      province_id: customerInfo.province_id,
      city_id: customerInfo.city_id,
      district_id: customerInfo.district_id,
      items: selectedCartItems,
      total: total,
      user_id: user?.id || null
    };

    const result = await api.createGuestOrder(orderData);
    
    if (result.success) {
      const selectedIds = selectedCartItems.map(item => item.id);
      onRemoveMultiple(selectedIds);
      setShowCheckoutModal(false);
      
      // Get province, city, district names
      const provinceName = customerInfo.province_name || provinces.find(p => p.id == customerInfo.province_id)?.name || '';
      const cityName = customerInfo.city_name || cities.find(c => c.id == customerInfo.city_id)?.name || '';
      // Use custom district name if entered, otherwise find from list
      const districtName = customerInfo.district_name || districts.find(d => d.id == customerInfo.district_id)?.name || '';
      
      // Set order details from backend response
      const details = {
        trackingId: result.tracking_id,
        name: customerInfo.name,
        phone: customerInfo.phone,
        email: customerInfo.email,
        address: customerInfo.address,
        province: provinceName,
        city: cityName,
        district: districtName,
        orderId: result.order_id
      };
      setOrderDetails(details);
      
      // Auto download receipt with full address
      const receiptText = `
========================================
        FARHAN ELECTRONICS
        Order Receipt
========================================

Tracking ID: ${result.tracking_id}
Order ID: ${result.order_id}

--- Customer Information ---
Name: ${customerInfo.name}
Phone: ${customerInfo.phone}
Email: ${customerInfo.email || 'N/A'}

--- Delivery Address ---
${customerInfo.address}
${districtName}, ${cityName}
${provinceName}, Pakistan

--- Order Items ---
${selectedCartItems.map(item => `${item.name} x ${item.quantity} = Rs. ${item.price * item.quantity}`).join('\n')}

--- Total Amount ---
Rs. ${total.toFixed(2)}

========================================
Thank you for your order!
Contact: 03217600273
Email: info@loomelectronicparts.com
========================================
      `;
      
      const blob = new Blob([receiptText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Order_${result.tracking_id}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setCustomerInfo({ name: '', phone: '', email: '', address: '', province_id: '', province_name: '', city_id: '', city_name: '', district_id: '', district_name: '' });
      setSelectedItems([]);
      setShowSuccessPopup(true);
    } else {
      alert('Order failed: ' + (result.message || 'Please try again.'));
    }
  };

  return (
    <div className="container">
      <div className="cart-container">
        <h1>Shopping Cart</h1>
        
        {cart.length === 0 ? (
          <div className="empty-cart">
            <h2>Your cart is empty</h2>
            <button className="btn" onClick={() => navigate('/')}>
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="cart-header">
              <label className="select-all">
                <input
                  type="checkbox"
                  checked={selectedItems.length === cart.length}
                  onChange={toggleSelectAll}
                />
                <span>Select All ({selectedItems.length}/{cart.length})</span>
              </label>
            </div>

            {cart.map((item) => (
              <div key={item.id} className={`cart-item ${selectedItems.includes(item.id) ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  className="item-checkbox"
                  checked={selectedItems.includes(item.id)}
                  onChange={() => toggleSelectItem(item.id)}
                />
                <div className="cart-item-info">
                  <div className="cart-item-name">{item.name}</div>
                  <div className="cart-item-price">Rs. {item.price}</div>
                </div>
                <div className="cart-item-actions">
                  <button className="qty-btn" onClick={() => onUpdateQuantity(item.id, -1)}>
                    -
                  </button>
                  <span style={{ margin: '0 15px', fontWeight: 'bold' }}>{item.quantity}</span>
                  <button className="qty-btn" onClick={() => onUpdateQuantity(item.id, 1)}>
                    +
                  </button>
                  <button className="qty-btn" onClick={() => onRemove(item.id)} style={{ marginLeft: '10px' }}>
                    🗑️
                  </button>
                </div>
              </div>
            ))}
            
            <div className="cart-total">
              <h3>Total ({selectedItems.length} items): Rs. {total.toFixed(2)}</h3>
              <button className="checkout-btn" onClick={handleCheckout}>
                Checkout Selected Items
              </button>
            </div>
          </>
        )}
      </div>

      {showCheckoutModal && (
        <div className="modal-overlay" onClick={() => setShowCheckoutModal(false)}>
          <div className="checkout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-header">
              <div>
                <h2>Complete Your Order</h2>
                <p>Fill in your delivery details</p>
              </div>
              <button className="modal-close" onClick={() => setShowCheckoutModal(false)}>×</button>
            </div>
            
            <div className="checkout-body">
              <div className="order-summary">
                <h3>Order Summary</h3>
                <div className="summary-items">
                  {selectedCartItems.map(item => (
                    <div key={item.id} className="summary-item">
                      <span>{item.name}</span>
                      <span>Rs. {item.price} × {item.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="summary-total">
                  <span>Total Amount</span>
                  <span>Rs. {total.toFixed(2)}</span>
                </div>
              </div>

              <form onSubmit={handleSubmitOrder} className="checkout-form">
                <div className="form-section">
                  <h3>Personal Information</h3>
                  <div className="form-row">
                    <div className="form-field">
                      <label>Full Name *</label>
                      <input
                        type="text"
                        placeholder="Enter your full name"
                        value={customerInfo.name}
                        onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-field">
                      <label>Phone Number *</label>
                      <input
                        type="tel"
                        placeholder="03XX-XXXXXXX"
                        value={customerInfo.phone}
                        onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Email Address</label>
                    <input
                      type="email"
                      placeholder="your.email@example.com"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3>Delivery Location</h3>
                  <div className="form-row">
                    <div className="form-field">
                      <label>Province *</label>
                      <Select
                        options={provinces.map(p => ({ value: p.id, label: p.name }))}
                        value={customerInfo.province_id ? { value: customerInfo.province_id, label: provinces.find(p => p.id == customerInfo.province_id)?.name } : null}
                        onChange={(option) => {
                          setCustomerInfo({
                            ...customerInfo,
                            province_id: option ? option.value : '',
                            province_name: option ? option.label : '',
                            city_id: '',
                            city_name: '',
                            district_id: '',
                            district_name: ''
                          });
                          if (!option) {
                            setCities([]);
                            setDistricts([]);
                          }
                        }}
                        placeholder="Select Province"
                        isClearable
                        isSearchable
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            padding: '4px',
                            background: '#f8f9fa',
                            border: state.isFocused ? '2px solid #2874f0' : '2px solid #e0e0e0',
                            borderRadius: '8px',
                            boxShadow: state.isFocused ? '0 0 0 4px rgba(40,116,240,0.1)' : 'none',
                            '&:hover': { borderColor: '#2874f0' }
                          }),
                          menu: (base) => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </div>
                    <div className="form-field">
                      <label>City *</label>
                      <Select
                        options={cities.map(c => ({ value: c.id, label: c.name }))}
                        value={customerInfo.city_id ? { value: customerInfo.city_id, label: cities.find(c => c.id == customerInfo.city_id)?.name } : null}
                        onChange={(option) => {
                          setCustomerInfo({
                            ...customerInfo,
                            city_id: option ? option.value : '',
                            city_name: option ? option.label : '',
                            district_id: '',
                            district_name: ''
                          });
                          if (!option) {
                            setDistricts([]);
                          }
                        }}
                        placeholder="Select City"
                        isClearable
                        isSearchable
                        isDisabled={!customerInfo.province_id}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            padding: '4px',
                            background: !customerInfo.province_id ? '#e9ecef' : '#f8f9fa',
                            border: state.isFocused ? '2px solid #2874f0' : '2px solid #e0e0e0',
                            borderRadius: '8px',
                            boxShadow: state.isFocused ? '0 0 0 4px rgba(40,116,240,0.1)' : 'none',
                            '&:hover': { borderColor: !customerInfo.province_id ? '#e0e0e0' : '#2874f0' }
                          }),
                          menu: (base) => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>District *</label>
                    <CreatableSelect
                      options={districts.map(d => ({ value: d.id, label: d.name }))}
                      value={customerInfo.district_name ? { value: customerInfo.district_id || customerInfo.district_name, label: customerInfo.district_name } : null}
                      onChange={(option) => {
                        if (option) {
                          const isExisting = districts.find(d => d.name === option.label);
                          setCustomerInfo({
                            ...customerInfo,
                            district_name: option.label,
                            district_id: isExisting ? option.value : ''
                          });
                        } else {
                          setCustomerInfo({
                            ...customerInfo,
                            district_name: '',
                            district_id: ''
                          });
                        }
                      }}
                      onCreateOption={(inputValue) => {
                        setCustomerInfo({
                          ...customerInfo,
                          district_name: inputValue,
                          district_id: ''
                        });
                      }}
                      placeholder="Select or type district"
                      isClearable
                      isSearchable
                      isDisabled={!customerInfo.city_id}
                      formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          padding: '4px',
                          background: !customerInfo.city_id ? '#e9ecef' : '#f8f9fa',
                          border: state.isFocused ? '2px solid #2874f0' : '2px solid #e0e0e0',
                          borderRadius: '8px',
                          boxShadow: state.isFocused ? '0 0 0 4px rgba(40,116,240,0.1)' : 'none',
                          '&:hover': { borderColor: !customerInfo.city_id ? '#e0e0e0' : '#2874f0' }
                        }),
                        menu: (base) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                    <small style={{color: '#666', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                      💡 Select from list or type your own district name
                    </small>
                  </div>
                  <div className="form-field">
                    <label>Complete Delivery Address *</label>
                    <textarea
                      placeholder="House/Flat No., Street, Area, Landmark"
                      value={customerInfo.address}
                      onChange={(e) => setCustomerInfo({...customerInfo, address: e.target.value})}
                      rows="4"
                      required
                    ></textarea>
                  </div>
                </div>

                <div className="checkout-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowCheckoutModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-place-order">
                    <span>Place Order</span>
                    <span>Rs. {total.toFixed(2)}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showSuccessPopup && orderDetails && (
        <div className="modal-overlay" style={{zIndex: 10000}}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px', textAlign: 'center'}}>
            <div style={{padding: '40px 20px'}}>
              <div style={{fontSize: '60px', marginBottom: '20px'}}>✅</div>
              <h2 style={{color: '#4caf50', marginBottom: '20px'}}>Order Placed Successfully!</h2>
              
              <div style={{background: '#f0f8ff', padding: '20px', borderRadius: '8px', marginBottom: '20px', textAlign: 'left'}}>
                <div style={{marginBottom: '12px'}}>
                  <strong>Tracking ID:</strong>
                  <div style={{fontSize: '24px', color: '#2196f3', fontWeight: 'bold', marginTop: '5px'}}>{orderDetails.trackingId}</div>
                </div>
                <div style={{marginBottom: '12px'}}>
                  <strong>Name:</strong> {orderDetails.name}
                </div>
                <div style={{marginBottom: '12px'}}>
                  <strong>Phone:</strong> {orderDetails.phone}
                </div>
                <div style={{marginBottom: '12px'}}>
                  <strong>Email:</strong> {orderDetails.email || 'N/A'}
                </div>
                <div style={{marginBottom: '12px'}}>
                  <strong>Delivery Address:</strong>
                  <div style={{marginTop: '5px', color: '#555'}}>
                    {orderDetails.address}<br/>
                    {orderDetails.district}, {orderDetails.city}<br/>
                    {orderDetails.province}, Pakistan
                  </div>
                </div>
              </div>
              
              <p style={{color: '#666', marginBottom: '20px'}}>Receipt has been downloaded automatically!</p>
              
              <button 
                className="btn" 
                onClick={() => {
                  setShowSuccessPopup(false);
                  setOrderDetails(null);
                  navigate('/');
                }}
                style={{width: '100%', padding: '12px'}}
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cart;
